/**
 * Brand setup: what the organization is looking for (kept in org.preferences.looking_for) and
 * how complete its profile is. The setup screen edits it; the dashboard shows the meter.
 */
import type { DealType } from '@/lib/dealTypes'

export type LookingFor = {
  deal_types: DealType[]
  sports: number[]
  /** Sports the brand typed that Athios doesn't list yet */
  other_sports: string[]
  states: string[]
  gender: 'any' | 'female' | 'male'
  /** Every range the brand pays in; it can pick more than one */
  budgets: string[]
  about: string | null
  /** Who the brand sells to, in their words: "women 35 to 70 who run" */
  customer: string | null
  /** The customer's age groups, as the audience buckets athletes' channels report */
  ages: string[]
}

export const EMPTY_LOOKING_FOR: LookingFor = {
  deal_types: [],
  sports: [],
  other_sports: [],
  states: [],
  gender: 'any',
  budgets: [],
  about: null,
  customer: null,
  ages: [],
}

/** The age groups a brand can target, matching the buckets athletes' audience data comes in */
export const AGE_GROUPS = [
  { id: '18-24', label: '18–24' },
  { id: '25-34', label: '25–34' },
  { id: '35-44', label: '35–44' },
  { id: '45-64', label: '45–64' },
  { id: '65-', label: '65+' },
]

export const BUDGETS = [
  { id: 'product_only', label: 'Gifting', blurb: 'Product, plus performance bonuses' },
  { id: 'under_500', label: 'Under $500', blurb: 'Per deliverable' },
  { id: '500_2000', label: '$500 to $2,000', blurb: 'Per deliverable' },
  { id: '2000_plus', label: '$2,000 and up', blurb: 'Per deliverable' },
]

export const GENDERS = [
  { id: 'any', label: 'Any' },
  { id: 'female', label: 'Women' },
  { id: 'male', label: 'Men' },
]

export const US_STATES: { code: string; name: string; region: string }[] = [
  { code: 'CT', name: 'Connecticut', region: 'Northeast' },
  { code: 'ME', name: 'Maine', region: 'Northeast' },
  { code: 'MA', name: 'Massachusetts', region: 'Northeast' },
  { code: 'NH', name: 'New Hampshire', region: 'Northeast' },
  { code: 'NJ', name: 'New Jersey', region: 'Northeast' },
  { code: 'NY', name: 'New York', region: 'Northeast' },
  { code: 'PA', name: 'Pennsylvania', region: 'Northeast' },
  { code: 'RI', name: 'Rhode Island', region: 'Northeast' },
  { code: 'VT', name: 'Vermont', region: 'Northeast' },
  { code: 'IL', name: 'Illinois', region: 'Midwest' },
  { code: 'IN', name: 'Indiana', region: 'Midwest' },
  { code: 'IA', name: 'Iowa', region: 'Midwest' },
  { code: 'KS', name: 'Kansas', region: 'Midwest' },
  { code: 'MI', name: 'Michigan', region: 'Midwest' },
  { code: 'MN', name: 'Minnesota', region: 'Midwest' },
  { code: 'MO', name: 'Missouri', region: 'Midwest' },
  { code: 'NE', name: 'Nebraska', region: 'Midwest' },
  { code: 'ND', name: 'North Dakota', region: 'Midwest' },
  { code: 'OH', name: 'Ohio', region: 'Midwest' },
  { code: 'SD', name: 'South Dakota', region: 'Midwest' },
  { code: 'WI', name: 'Wisconsin', region: 'Midwest' },
  { code: 'AL', name: 'Alabama', region: 'South' },
  { code: 'AR', name: 'Arkansas', region: 'South' },
  { code: 'DE', name: 'Delaware', region: 'South' },
  { code: 'DC', name: 'Washington, DC', region: 'South' },
  { code: 'FL', name: 'Florida', region: 'South' },
  { code: 'GA', name: 'Georgia', region: 'South' },
  { code: 'KY', name: 'Kentucky', region: 'South' },
  { code: 'LA', name: 'Louisiana', region: 'South' },
  { code: 'MD', name: 'Maryland', region: 'South' },
  { code: 'MS', name: 'Mississippi', region: 'South' },
  { code: 'NC', name: 'North Carolina', region: 'South' },
  { code: 'OK', name: 'Oklahoma', region: 'South' },
  { code: 'SC', name: 'South Carolina', region: 'South' },
  { code: 'TN', name: 'Tennessee', region: 'South' },
  { code: 'TX', name: 'Texas', region: 'South' },
  { code: 'VA', name: 'Virginia', region: 'South' },
  { code: 'WV', name: 'West Virginia', region: 'South' },
  { code: 'AK', name: 'Alaska', region: 'West' },
  { code: 'AZ', name: 'Arizona', region: 'West' },
  { code: 'CA', name: 'California', region: 'West' },
  { code: 'CO', name: 'Colorado', region: 'West' },
  { code: 'HI', name: 'Hawaii', region: 'West' },
  { code: 'ID', name: 'Idaho', region: 'West' },
  { code: 'MT', name: 'Montana', region: 'West' },
  { code: 'NV', name: 'Nevada', region: 'West' },
  { code: 'NM', name: 'New Mexico', region: 'West' },
  { code: 'OR', name: 'Oregon', region: 'West' },
  { code: 'UT', name: 'Utah', region: 'West' },
  { code: 'WA', name: 'Washington', region: 'West' },
  { code: 'WY', name: 'Wyoming', region: 'West' },
]

/** The sports shown as quick picks; anything else is typed in */
export const POPULAR_SPORTS = [
  'Football',
  'Basketball',
  'Baseball',
  'Soccer',
  'Volleyball',
  'Track & Field',
]

export const REGIONS = ['Northeast', 'Midwest', 'South', 'West']

export const lookingForOf = (org: any): LookingFor => {
  const saved = org?.preferences?.looking_for ?? {}
  // Profiles saved before pay became multi-select kept a single `budget`
  const budgets: string[] = Array.isArray(saved.budgets)
    ? saved.budgets
    : saved.budget
      ? [saved.budget]
      : []
  const { budget: _old, ...rest } = saved
  return { ...EMPTY_LOOKING_FOR, ...rest, budgets }
}

export type SetupItem = { id: string; label: string; done: boolean; points: number }

/** The brand profile meter: which pieces are in place, out of 100 */
export function brandSetupItems(org: any): SetupItem[] {
  const lf = lookingForOf(org)
  const brandCount = Number(org?.brandCount ?? 0)
  return [
    { id: 'brand', label: 'A brand with a logo', done: brandCount > 0, points: 25 },
    { id: 'about', label: 'What your brand is about', done: !!lf.about, points: 15 },
    {
      id: 'deal_types',
      label: 'What you want to run',
      done: lf.deal_types.length > 0,
      points: 15,
    },
    {
      id: 'customer',
      label: 'Who your brand is for',
      done: !!lf.customer || lf.ages.length > 0 || lf.gender !== 'any',
      points: 15,
    },
    { id: 'states', label: 'Where your athletes are', done: lf.states.length > 0, points: 15 },
    { id: 'budget', label: 'What you usually pay', done: lf.budgets.length > 0, points: 15 },
  ]
}

export const brandSetupScore = (org: any) =>
  brandSetupItems(org).reduce((sum, i) => sum + (i.done ? i.points : 0), 0)

/** A brand-new org: no brand yet and nothing about what it wants. Setup comes first. */
export const needsBrandSetup = (org: any) =>
  !!org && Number(org.brandCount ?? 0) === 0 && !org.preferences?.looking_for
